<div class="rich-text-editor" [class.is-invalid]="hasErrors" [class.is-valid]="touched && !hasErrors">

    <div class="rich-text-toolbar" role="toolbar" aria-label="Formatting">

        <!-- Font family -->
        <div class="dropdown">
            <button type="button" class="rich-text-toolbar-button"
                [class.is-active]="!!currentFontFamily()"
                data-bs-toggle="dropdown" aria-expanded="false"
                aria-label="Font family" title="Font family">
                <fa-duotone-icon icon="font-case" />
                <fa-duotone-icon icon="caret-down" secondaryOpacity="0.9" class="ms-1 rich-text-caret" />
            </button>
            <ul class="dropdown-menu">
                @for (family of fontFamilyOptions; track family.value) {
                    <li>
                        <button type="button" class="dropdown-item"
                            [class.active]="(currentFontFamily() ?? '') === family.value"
                            (click)="setFontFamily(family.value)">
                            <span [style.font-family]="family.value || null">{{ family.name }}</span>
                        </button>
                    </li>
                }
            </ul>
        </div>

        <!-- Font size -->
        <div class="dropdown">
            <button type="button" class="rich-text-toolbar-button"
                [class.is-active]="!!currentFontSize()"
                data-bs-toggle="dropdown" aria-expanded="false"
                aria-label="Font size" title="Font size">
                <fa-duotone-icon icon="text-size" />
                <fa-duotone-icon icon="caret-down" secondaryOpacity="0.9" class="ms-1 rich-text-caret" />
            </button>
            <ul class="dropdown-menu">
                @for (size of fontSizeOptions; track size.value) {
                    <li>
                        <button type="button" class="dropdown-item"
                            [class.active]="(currentFontSize() ?? '') === size.value"
                            (click)="setFontSize(size.value)">
                            <span [style.font-size]="size.value || null">{{ size.name }}</span>
                        </button>
                    </li>
                }
            </ul>
        </div>

        <!-- Font color -->
        <div class="dropdown">
            <button type="button" class="rich-text-toolbar-button rich-text-color-button"
                [class.is-active]="!!currentColor()"
                data-bs-toggle="dropdown" data-bs-auto-close="outside" aria-expanded="false"
                aria-label="Font color" title="Font color">
                <fa-duotone-icon icon="font" />
                <span class="rich-text-color-swatch" [style.background-color]="currentColor() || 'transparent'"></span>
                <fa-duotone-icon icon="caret-down" secondaryOpacity="0.9" class="ms-1 rich-text-caret" />
            </button>
            <div class="dropdown-menu p-2 rich-text-color-menu">
                <div class="rich-text-color-grid">
                    @for (color of colorPalette; track color.value) {
                        <button type="button" class="rich-text-color-cell"
                            [class.active]="currentColor() === color.value"
                            [class.rich-text-color-cell--default]="!color.value"
                            [style.background-color]="color.value || 'transparent'"
                            (click)="setColor(color.value)"
                            [attr.aria-label]="color.name"
                            [title]="color.name">
                            @if (!color.value) {
                                <fa-duotone-icon icon="ban" secondaryOpacity="1" />
                            }
                        </button>
                    }
                </div>
                <label class="rich-text-color-custom mt-2 d-flex align-items-center gap-2">
                    <input type="color" class="rich-text-color-input"
                        [value]="currentColor() || '#000000'"
                        (input)="onCustomColorInput($event)" />
                    <span class="small">Custom color</span>
                </label>
            </div>
        </div>

        <!-- Highlight -->
        <div class="dropdown">
            <button type="button" class="rich-text-toolbar-button rich-text-color-button"
                [class.is-active]="isHighlightActive()"
                data-bs-toggle="dropdown" data-bs-auto-close="outside" aria-expanded="false"
                aria-label="Highlight" title="Highlight">
                <fa-duotone-icon icon="highlighter" />
                <span class="rich-text-color-swatch" [style.background-color]="currentHighlight() || 'transparent'"></span>
                <fa-duotone-icon icon="caret-down" secondaryOpacity="0.9" class="ms-1 rich-text-caret" />
            </button>
            <div class="dropdown-menu p-2 rich-text-color-menu">
                <div class="rich-text-color-grid">
                    @for (color of highlightPalette; track color.value) {
                        <button type="button" class="rich-text-color-cell"
                            [class.active]="(currentHighlight() ?? '') === color.value"
                            [class.rich-text-color-cell--default]="!color.value"
                            [style.background-color]="color.value || 'transparent'"
                            (click)="setHighlight(color.value)"
                            [attr.aria-label]="color.name"
                            [title]="color.name">
                            @if (!color.value) {
                                <fa-duotone-icon icon="ban" secondaryOpacity="1" />
                            }
                        </button>
                    }
                </div>
                <label class="rich-text-color-custom mt-2 d-flex align-items-center gap-2">
                    <input type="color" class="rich-text-color-input"
                        [value]="currentHighlight() || '#ffff00'"
                        (input)="onCustomHighlightInput($event)" />
                    <span class="small">Custom color</span>
                </label>
            </div>
        </div>

        <span class="rich-text-toolbar-divider" aria-hidden="true"></span>

        <!-- Inline text formatting -->
        <button type="button" class="rich-text-toolbar-button"
            [class.is-active]="isBoldActive()"
            (click)="toggleBold()" aria-label="Bold" title="Bold">
            <fa-duotone-icon icon="bold" />
        </button>
        <button type="button" class="rich-text-toolbar-button"
            [class.is-active]="isItalicActive()"
            (click)="toggleItalic()" aria-label="Italic" title="Italic">
            <fa-duotone-icon icon="italic" />
        </button>
        <button type="button" class="rich-text-toolbar-button"
            [class.is-active]="isUnderlineActive()"
            (click)="toggleUnderline()" aria-label="Underline" title="Underline">
            <fa-duotone-icon icon="underline" />
        </button>
        <button type="button" class="rich-text-toolbar-button"
            [class.is-active]="isStrikeActive()"
            (click)="toggleStrike()" aria-label="Strikethrough" title="Strikethrough">
            <fa-duotone-icon icon="strikethrough" />
        </button>

        <span class="rich-text-toolbar-divider" aria-hidden="true"></span>

        <!-- Headings — toggling the active level off returns the block to a paragraph, so no separate
             paragraph button is needed. -->
        <div class="dropdown">
            <button type="button" class="rich-text-toolbar-button"
                [class.is-active]="isAnyHeadingActive()"
                data-bs-toggle="dropdown" aria-expanded="false"
                aria-label="Heading level" title="Heading level">
                <fa-duotone-icon icon="h1" />
                <fa-duotone-icon icon="caret-down" secondaryOpacity="0.9" class="ms-1 rich-text-caret" />
            </button>
            <ul class="dropdown-menu">
                <li>
                    <button type="button" class="dropdown-item"
                        [class.active]="isHeading1Active()"
                        (click)="toggleHeading(1)">
                        <fa-duotone-icon icon="h1" class="me-2" />Heading 1
                    </button>
                </li>
                <li>
                    <button type="button" class="dropdown-item"
                        [class.active]="isHeading2Active()"
                        (click)="toggleHeading(2)">
                        <fa-duotone-icon icon="h2" class="me-2" />Heading 2
                    </button>
                </li>
                <li>
                    <button type="button" class="dropdown-item"
                        [class.active]="isHeading3Active()"
                        (click)="toggleHeading(3)">
                        <fa-duotone-icon icon="h3" class="me-2" />Heading 3
                    </button>
                </li>
                <li>
                    <button type="button" class="dropdown-item"
                        [class.active]="isHeading4Active()"
                        (click)="toggleHeading(4)">
                        <fa-duotone-icon icon="h4" class="me-2" />Heading 4
                    </button>
                </li>
                <li>
                    <button type="button" class="dropdown-item"
                        [class.active]="isHeading5Active()"
                        (click)="toggleHeading(5)">
                        <fa-duotone-icon icon="h5" class="me-2" />Heading 5
                    </button>
                </li>
                <li>
                    <button type="button" class="dropdown-item"
                        [class.active]="isHeading6Active()"
                        (click)="toggleHeading(6)">
                        <fa-duotone-icon icon="h6" class="me-2" />Heading 6
                    </button>
                </li>
            </ul>
        </div>

        <!-- Text alignment -->
        <div class="dropdown">
            <button type="button" class="rich-text-toolbar-button"
                [class.is-active]="currentAlignment() !== 'left'"
                data-bs-toggle="dropdown" aria-expanded="false"
                aria-label="Text alignment" title="Text alignment">
                <fa-duotone-icon [icon]="alignmentIcon()" />
                <fa-duotone-icon icon="caret-down" class="ms-1 rich-text-caret" />
            </button>
            <ul class="dropdown-menu">
                @for (option of alignmentOptions; track option.value) {
                    <li>
                        <button type="button" class="dropdown-item"
                            [class.active]="currentAlignment() === option.value"
                            (click)="setAlignment(option.value)">
                            <fa-duotone-icon [icon]="option.icon" class="me-2" />{{ option.name }}
                        </button>
                    </li>
                }
            </ul>
        </div>

        <!-- Lists & block elements -->
        <button type="button" class="rich-text-toolbar-button"
            [class.is-active]="isBulletListActive()"
            (click)="toggleBulletList()" aria-label="Bulleted list" title="Bulleted list">
            <fa-duotone-icon icon="list-ul" />
        </button>
        <button type="button" class="rich-text-toolbar-button"
            [class.is-active]="isOrderedListActive()"
            (click)="toggleOrderedList()" aria-label="Numbered list" title="Numbered list">
            <fa-duotone-icon icon="list-ol" />
        </button>
        <button type="button" class="rich-text-toolbar-button"
            [class.is-active]="isBlockquoteActive()"
            (click)="toggleBlockquote()" aria-label="Blockquote" title="Blockquote">
            <fa-duotone-icon icon="quote-left" secondaryOpacity="1" />
        </button>
        <button type="button" class="rich-text-toolbar-button"
            (click)="setHorizontalRule()" aria-label="Horizontal rule" title="Horizontal rule">
            <fa-duotone-icon icon="horizontal-rule" />
        </button>
        <button type="button" class="rich-text-toolbar-button"
            (click)="setHardBreak()" aria-label="Line break" title="Line break">
            <fa-duotone-icon icon="arrow-turn-down-left" secondaryOpacity="1" />
        </button>

        <span class="rich-text-toolbar-divider" aria-hidden="true"></span>

        <!-- Hyperlink — always enabled. With a selection, Text prefills from it; with an image selected,
             the link wraps the image; with no selection at all, saving inserts a new linked text run at
             the caret. -->
        <button type="button" class="rich-text-toolbar-button"
            [class.is-active]="isLinkActive()"
            (mousedown)="$event.preventDefault()"
            (click)="startLinkEdit()" aria-label="Insert link" title="Insert link">
            <fa-duotone-icon icon="link" />
        </button>

        @if (allowImageInsert()) {
            <!-- Opens the Document Center picker restricted to Public images and inserts the selected file's CDN URL. -->
            <button type="button" class="rich-text-toolbar-button"
                (click)="openImagePicker()" aria-label="Insert image" title="Insert image">
                <fa-duotone-icon icon="image" />
            </button>

            <!-- Image text-wrap dropdown — only enabled while an image is selected. Left/right float the image so
                 surrounding paragraph text flows around it; to center an image, center its containing paragraph. -->
            <div class="dropdown">
                <button type="button" class="rich-text-toolbar-button"
                    [class.is-active]="!!currentImageAlign()"
                    [disabled]="!isImageSelected()"
                    data-bs-toggle="dropdown" aria-expanded="false"
                    aria-label="Image text wrap" title="Image text wrap">
                    <fa-duotone-icon [icon]="imageWrapIcon()" />
                    <fa-duotone-icon icon="caret-down" secondaryOpacity="0.9" class="ms-1 rich-text-caret" />
                </button>
                <ul class="dropdown-menu">
                    @for (option of imageWrapOptions; track option.value) {
                        <li>
                            <button type="button" class="dropdown-item"
                                [class.active]="(currentImageAlign() ?? '') === option.value"
                                (click)="setImageAlign(option.value)">
                                <fa-duotone-icon [icon]="option.icon" class="me-2" />{{ option.name }}
                            </button>
                        </li>
                    }
                </ul>
            </div>
        }

        <span class="rich-text-toolbar-divider" aria-hidden="true"></span>

        <!-- Clear formatting & history -->
        <button type="button" class="rich-text-toolbar-button"
            (click)="selectAll()" aria-label="Select all" title="Select all">
            <fa-duotone-icon icon="check-double" />
        </button>
        <button type="button" class="rich-text-toolbar-button"
            (click)="clearFormatting()" aria-label="Clear formatting" title="Clear formatting">
            <fa-duotone-icon icon="text-slash" secondaryOpacity="1" />
        </button>
        <button type="button" class="rich-text-toolbar-button"
            (click)="undo()" aria-label="Undo" title="Undo">
            <fa-duotone-icon icon="rotate-left" secondaryOpacity="1" />
        </button>
        <button type="button" class="rich-text-toolbar-button"
            (click)="redo()" aria-label="Redo" title="Redo">
            <fa-duotone-icon icon="rotate-right" secondaryOpacity="1" />
        </button>

    </div>

    <div #editorHost class="rich-text-content"></div>

    <!-- Bubble menu shown when the caret/selection is inside a link mark, OR while the user is actively editing
         a link via the toolbar. Tiptap's BubbleMenu plugin positions and toggles this element. -->
    <div #linkBubbleMenu class="rich-text-link-bubble" [class.rich-text-link-bubble--editing]="isEditingLink()"
        role="toolbar" aria-label="Link actions">
        @if (isEditingLink()) {
            @if (!isImageSelected()) {
                <ms-input-label [isRequired]="true" [labelText]="'Text'" />
                <input #linkTextInput type="text" class="form-control form-control-sm rich-text-link-bubble-input"
                    placeholder="Link text"
                    [value]="linkTextValue()"
                    (input)="onLinkTextInput($event)"
                    (keydown)="onLinkEditKeydown($event)"
                    (blur)="onLinkEditBlur($event)" />
            }
            <ms-input-label [isRequired]="true" [labelText]="'URL'" [class.mt-2]="!isImageSelected()" />
            <input #linkEditInput type="url" class="form-control form-control-sm rich-text-link-bubble-input"
                placeholder="https://example.com"
                [value]="linkEditValue()"
                (input)="onLinkEditInput($event)"
                (keydown)="onLinkEditKeydown($event)"
                (blur)="onLinkEditBlur($event)" />
            <div class="rich-text-link-bubble-actions">
                <button type="button" class="rich-text-toolbar-button"
                    [disabled]="!isLinkEditValid()"
                    (mousedown)="$event.preventDefault()"
                    (click)="applyLinkEdit()" aria-label="Apply link" title="Apply link">
                    <fa-duotone-icon icon="check" />
                </button>
                <button type="button" class="rich-text-toolbar-button"
                    (mousedown)="$event.preventDefault()"
                    (click)="cancelLinkEdit()" aria-label="Cancel" title="Cancel">
                    <fa-duotone-icon icon="xmark" />
                </button>
            </div>
        } @else {
            <a class="rich-text-link-bubble-href" [href]="bubbleLinkHref()" target="_blank" rel="noopener noreferrer"
                [title]="bubbleLinkHref()">{{ bubbleLinkHref() }}</a>
            <button type="button" class="rich-text-toolbar-button"
                (mousedown)="$event.preventDefault()"
                (click)="openBubbleLink()" aria-label="Open link" title="Open link">
                <fa-duotone-icon icon="arrow-up-right-from-square" />
            </button>
            <button type="button" class="rich-text-toolbar-button"
                (mousedown)="$event.preventDefault()"
                (click)="startLinkEdit()" aria-label="Edit link" title="Edit link">
                <fa-duotone-icon icon="pen" />
            </button>
            <button type="button" class="rich-text-toolbar-button"
                (mousedown)="$event.preventDefault()"
                (click)="removeBubbleLink()" aria-label="Remove link" title="Remove link">
                <fa-duotone-icon icon="link-slash" />
            </button>
            <button type="button" class="rich-text-toolbar-button"
                (mousedown)="$event.preventDefault()"
                (click)="closeBubble()" aria-label="Close" title="Close">
                <fa-duotone-icon icon="xmark" />
            </button>
        }
    </div>

    @if (allowImageInsert()) {
        <!-- Document Center picker — grid layout so image thumbnails preview before insertion. -->
        <ms-document-file-picker
            #imagePicker
            [modalId]="imagePickerModalId"
            title="Insert image"
            helpText="Choose a folder, then select an image."
            confirmLabel="Insert"
            layout="grid"
            [permissions]="imagePickerPermissions"
            [fileTypes]="imagePickerFileTypes"
            (fileSelected)="onImageFileSelected($event)" />
    }

</div>
